import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { ColumnDataType, ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { LocalDate } from '../../../core/model/local-date';
import { InvoiceReminder } from '../../model/invoice-reminder';
import { InvoiceReminderCommand } from '../../command/invoice-reminder';
import { InvoiceReminderService } from '../../service/data/invoice-reminder.service';
import { InvoiceReminderCommandHandlerResolver } from '../../service/command/invoice-reminder-command-handler-resolver.service';

@Component({
    selector: 'app-invoice-reminder-form',
    templateUrl: './invoice-reminder-form.component.html',
    styleUrls: ['./invoice-reminder-form.component.scss'],
    providers: [InvoiceReminderCommandHandlerResolver, { provide: DataService, useExisting: InvoiceReminderService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoiceReminderFormComponent extends FormComponentBase<InvoiceReminder> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    cdf: ColumnDefinitionFactory,
    handlerResolver: InvoiceReminderCommandHandlerResolver,
    service: InvoiceReminderService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);

    this.addDeleteMenu = false;
    const defaultOrder = 'Number';

    this.addMenuItemDefinition('COMMON.OPEN_INVOICE', InvoiceReminderCommand.openInvoice);
    this.addMenuItemDefinition('INVOICE.COMMAND.GETPDF', InvoiceReminderCommand.previewReminder);

    this.columnDefinitions = [
      cdf.createDateTime('COMMON.CREATED_DATE', 'createdDate'),
      cdf.create('COMMON.RECEIVER', 'contactName'),
      cdf.createDateTime('COMMON.SENT', 'sentDate'),
      cdf.create('INVOICE.DELIVERY_METHOD', 'reminderDeliveryMethodText'),
      cdf.create('COMMON.EMAIL_DELIVERY_STATUS', 'deliveryStatusText', {
        visibility: ColumnVisibility.default,
        serverSideSortBinding: defaultOrder,
        columnDataType: ColumnDataType.translation,
        helpBuilder: (ir: InvoiceReminder) => ir.emailDeliveryInfo,
      }),
    ];
  }

  columnDefinitions: ColumnDefinition[];

  // app-date-picker [model] expects a LocalDate; the model exposes Date for these fields.
  readonly LocalDate = LocalDate;

  protected createModel(): InvoiceReminder {
    return new InvoiceReminder();
  }

  hasReminderFees: boolean;
  hasGuarantors: boolean;
  hasOcr: boolean;

  protected getFormGroup(): FormGroup {
    this.hasOcr = this.dataContext.ocr !== undefined && this.dataContext.ocr !== '';

    return this._fb.group({
      invoiceId: this._fc.createHidden(this.dataContext.invoiceId),
      contactName: this._fc.createReadonly('INVOICE.RECIPIENT_NAME', this.dataContext.invoiceRecipient),
      invoiceTotalAmount: this._fc.createReadonly('INVOICE.INVOICEAMOUNT', this.dataContext.invoiceTotalAmount),
      invoiceUnPaidAmount: this._fc.createReadonly('INVOICE.REMAINING_AMOUNT', this.dataContext.invoiceUnPaidAmount),
      emailDeliveryStatusText: this._fc.createReadonly('COMMON.EMAIL_DELIVERY_STATUS', this.dataContext.emailDeliveryStatusText),
      deliveryMethod: this._fc.createReadonly('INVOICE.DELIVERY_METHOD', this.dataContext.reminderDeliveryMethodText),
      companyName: this._fc.createReadonly('COMMON.COMPANY', this.dataContext.companyNumberAndName),
      ocr: this._fc.createReadonly('INVOICE.OCRNUMBER', this.dataContext.ocr),
    });
  }

  showGuarantors: boolean;

  protected init(data: InvoiceReminder) {
    super.init(data);
    this.showGuarantors = data.guarantorReminders && data.guarantorReminders.length > 0;
  }
}
